// ============================================================================
// API Abstraction Layer for Lambda Functions
// ============================================================================
// This service communicates with AWS Lambda functions via API Gateway.
// All image processing is done in-memory without persistent storage.
// ============================================================================

import axios from 'axios';
import { generateAllCrops } from '../utils/cropSimulator';
import { generatePreviewsFromBboxes } from '../utils/cropFromBbox';

// Configure base URL from environment or default to local
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 120000, // 2 minute timeout for large image processing
  withCredentials: false // Set to false for CORS with '*' origin
});

class ProcessService {
  /**
   * Resize image to maximum dimension while preserving aspect ratio
   */
  async resizeImage(file, maxSize = 512) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          // Only resize if image is larger than maxSize
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Use better quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with JPEG format for smaller size
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                console.log(`Image resized from ${img.width}x${img.height} to ${width}x${height}`);
                console.log(`File size: ${(file.size / 1024).toFixed(2)}KB → ${(blob.size / 1024).toFixed(2)}KB`);
                resolve(resizedFile);
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            },
            'image/jpeg',
            0.9 // 90% quality for good balance of size and quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Resize mask images for faster selection processing
   * Masks are base64 PNG images that need to be resized
   */
  async resizeMaskForSelection(maskBase64, maxSize = 128) {
    return new Promise((resolve, reject) => {
      // Create image from base64
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        // Only resize if image is larger than maxSize
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Use better quality settings for masks
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert back to base64 PNG (keep as PNG for masks)
        const resizedBase64 = canvas.toDataURL('image/png').split(',')[1];
        console.log(`Mask resized from ${img.width}x${img.height} to ${width}x${height} for selection`);
        resolve(resizedBase64);
      };
      
      img.onerror = () => reject(new Error('Failed to load mask image'));
      
      // Load the base64 mask (add data URL prefix if needed)
      if (maskBase64.startsWith('data:')) {
        img.src = maskBase64;
      } else {
        img.src = `data:image/png;base64,${maskBase64}`;
      }
    });
  }

  /**
   * Convert file to base64 string
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Detect image format from file
   */
  getImageFormat(file) {
    const type = file.type;
    if (type === 'image/png') return 'image/png';
    if (type === 'image/gif') return 'image/gif';
    if (type === 'image/webp') return 'image/webp';
    return 'image/jpeg'; // Default to JPEG for jpg, jpeg, and unknown types
  }

  /**
   * Invoke a Lambda function via API Gateway
   */
  async invoke(functionName, payload) {
    try {
      const response = await api.post(`/api/${functionName}`, payload);
      
      // Parse response body if it's a string
      if (typeof response.data === 'string') {
        return JSON.parse(response.data);
      }
      
      // Handle Lambda proxy response format
      if (response.data.body) {
        return JSON.parse(response.data.body);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error invoking ${functionName}:`, error);
      throw error;
    }
  }

  /**
   * Process an image through all Lambda functions
   * Executes analyze-image and generate-masks in parallel,
   * then select-best-mask, with client-side crop generation
   */
  async processImage(file, onProgress) {
    try {
      // Resize image first to reduce payload size
      onProgress({ stage: 'encoding', progress: 5, message: 'Resizing image...' });
      const resizedFile = await this.resizeImage(file, 512);
      
      // Convert resized file to base64
      onProgress({ stage: 'encoding', progress: 10, message: 'Preparing image...' });
      const base64Image = await this.fileToBase64(resizedFile);
      const imageFormat = 'image/jpeg'; // Always JPEG after resize
      
      // Step 1: Analyze Image AND Generate Masks in PARALLEL
      onProgress({ stage: 'analyzing', progress: 20, message: 'Analyzing image...' });
      
      // Start both API calls in parallel but handle results independently
      const analysisPromise = this.invoke('analyze-image', { 
        image_bytes: base64Image,
        image_format: imageFormat
      }).then(result => {
        // Send analysis result immediately when ready
        onProgress({ 
          stage: 'analyzing', 
          progress: 35,
          message: 'Analysis complete, generating masks...',
          data: { mainObject: result.analysis }
        });
        return result;
      });
      
      const masksPromise = this.invoke('generate-masks', { 
        image_bytes: base64Image,
        image_format: imageFormat
      });
      
      // Wait for both to complete
      const [analysisResult, masksResult] = await Promise.all([
        analysisPromise,
        masksPromise
      ]);
      
      const { analysis } = analysisResult;
      const { bboxes } = masksResult;  // Only extracting bboxes now
      
      // Generate mask previews from bboxes
      const maskPreviews = await generatePreviewsFromBboxes(base64Image, bboxes);
      
      // Send masks data when ready
      onProgress({ 
        stage: 'segmenting', 
        progress: 50, 
        message: `Detected ${bboxes.length} objects`,
        data: { 
          mainObject: analysis, // Keep sending this to ensure it's set
          totalMasks: bboxes.length,
          masks: maskPreviews
        }
      });
      
      // Step 2: Select Best Mask
      onProgress({ stage: 'selecting', progress: 60, message: 'Selecting best mask...' });
      
      // Send original image and bboxes to Lambda for selection
      const maskSelection = await this.invoke('select-best-mask', {
        analysis: analysis,
        image_bytes: base64Image,  // Original image
        bboxes: bboxes            // Raw bbox coordinates
      });
      
      let selected_index = maskSelection.selected_index;
      
      // Handle -1 error case
      if (selected_index === -1) {
        console.error('Mask selection failed:', maskSelection.error);
        console.warn('Defaulting to first mask (index 0)');
        selected_index = 0;
        
        onProgress({ 
          stage: 'selecting', 
          progress: 70, 
          message: `Selection failed: ${maskSelection.error}. Using first mask.`,
          data: { 
            selectedMaskIndex: 1,
            error: maskSelection.error
          }
        });
      } else {
        onProgress({ 
          stage: 'selecting', 
          progress: 70, 
          message: `Selected mask #${selected_index + 1}`,
          data: { selectedMaskIndex: selected_index + 1 }
        });
      }
      
      // Step 3: Generate Crops (Client-side simulation)
      onProgress({ stage: 'cropping', progress: 80, message: 'Creating cropped variations...' });
      
      // Use the bounding box of the selected detection to generate crops
      const selectedBbox = bboxes[selected_index];
      
      // Generate all aspect ratio crops using the simulator
      const transformedCrops = await generateAllCrops(base64Image, selectedBbox);
      
      console.log(`Generated ${transformedCrops.length} crop variations client-side`);
      
      onProgress({ 
        stage: 'complete', 
        progress: 100, 
        message: 'Processing complete!',
        data: {
          mainObject: analysis,
          totalMasks: bboxes.length,
          crops: transformedCrops
        }
      });
      
      return {
        analysis,
        total_masks: bboxes.length,
        selected_index,
        crops: transformedCrops,
        masks: maskPreviews.map(m => m.preview),
        bboxes: bboxes,
        originalImage: base64Image
      };
      
    } catch (error) {
      console.error('Error processing image:', error);
      throw error;
    }
  }

  /**
   * Process image with separate function calls (for testing/debugging)
   */
  async analyzeImage(file) {
    const resizedFile = await this.resizeImage(file, 512);
    const base64Image = await this.fileToBase64(resizedFile);
    return this.invoke('analyze-image', { 
      image_bytes: base64Image,
      image_format: 'image/jpeg'
    });
  }

  async generateMasks(file) {
    const resizedFile = await this.resizeImage(file, 512);
    const base64Image = await this.fileToBase64(resizedFile);
    const result = await this.invoke('generate-masks', { 
      image_bytes: base64Image,
      image_format: 'image/jpeg'
    });
    
    // Generate previews from bboxes for backward compatibility
    if (result.bboxes) {
      const previews = await generatePreviewsFromBboxes(base64Image, result.bboxes);
      result.masks = previews.map(p => p.preview.split(',')[1]);
    }
    
    return result;
  }

  async selectBestMask(analysis, imageBase64, bboxes) {
    // Send original image and bboxes for server-side processing
    return this.invoke('select-best-mask', { 
      analysis, 
      image_bytes: imageBase64,
      bboxes: bboxes
    });
  }


  /**
   * Get image URL (for base64 display)
   */
  getImageUrl(base64OrPath) {
    if (base64OrPath.startsWith('data:')) {
      return base64OrPath;
    }
    // For backward compatibility
    return base64OrPath;
  }

  /**
   * Get crop URL (for base64 display)
   */
  getCropUrl(jobId, base64Data) {
    if (base64Data && base64Data.startsWith('data:')) {
      return base64Data;
    }
    // Return as data URL if it's raw base64
    return `data:image/jpeg;base64,${base64Data}`;
  }

  /**
   * Download a single crop
   */
  downloadCrop(cropName, base64Data) {
    // Create a blob from base64
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cropName}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download all crops as individual files
   */
  async downloadAllCrops(crops) {
    for (const crop of crops) {
      this.downloadCrop(crop.filename.replace('.jpg', ''), crop.base64);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

}

const processService = new ProcessService();
export default processService;